import * as React from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DocumentRef, FindHint } from './types';
import { usePdfDocument } from './pdf/use-pdf-document';
import { usePdfPage } from './pdf/use-pdf-page';
import { useViewport } from './viewport/use-viewport';
import { useImageSession } from './image/use-image-session';
import { buildOcrOverlayBoxes, useOcr } from './ocr/use-ocr';
import { useLocate } from './locate/use-locate';
import { LocatePanel } from './locate/locate-panel';
import { ViewerToolbar } from './chrome/toolbar';
import { ViewerStage } from './chrome/stage';
import './text-layer.css';
import './viewer.css';

export interface DocumentViewerFilePager {
    label: string;
    current: number;
    total: number;
    canPrev: boolean;
    canNext: boolean;
    loading?: boolean;
    onPrev: () => void;
    onNext: () => void;
}

export interface DocumentViewerProps {
    url: string;
    documentRef?: DocumentRef;
    height?: string | number;
    showOcrStub?: boolean;
    findHints?: FindHint[];
    showLocate?: boolean;
    allowOcr?: boolean;
    allowRotate?: boolean;
    autoRunOcr?: boolean;
    fileName?: string;
    onOrientedSave?: (blob: Blob, fileName: string) => void | Promise<void>;
    verifyLabel?: string;
    autoSelectRequired?: boolean;
    filePager?: DocumentViewerFilePager;
    fileActions?: React.ReactNode;
    toolbarHost?: HTMLElement | null;
    previewContent?: React.ReactNode;
}

const DocumentViewer = (props: DocumentViewerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const onResetRef = useRef(() => { });
    const onCancelRenderRef = useRef(() => { });
    const paintHighlightsRef = useRef(() => { });
    const fitImageRef = useRef(() => { });
    const onStatusRef = useRef((_s: string) => { });
    const onAlignFinishedRef = useRef((_should: boolean) => { });
    const onClearFindRef = useRef(() => { });

    const pdf = usePdfDocument(props.url, onResetRef, onCancelRenderRef);

    const viewport = useViewport({
        containerRef,
        loading: pdf.loading,
        error: pdf.error,
        mimeKind: pdf.mimeKind,
        fitImageToStage: () => fitImageRef.current()
    });

    const image = useImageSession({
        url: props.url,
        mimeKind: pdf.mimeKind,
        loading: pdf.loading,
        allowRotate: props.allowRotate,
        allowOcr: props.allowOcr,
        autoRunOcr: props.autoRunOcr,
        fileName: props.fileName,
        onOrientedSave: props.onOrientedSave,
        containerRef,
        scale: viewport.scale,
        setScale: viewport.setScale,
        panRef: viewport.panRef,
        setPan: viewport.setPan,
        lastFitRef: viewport.lastFitRef,
        setFitScale: viewport.setFitScale,
        onStatus: (s) => onStatusRef.current(s),
        onAlignFinished: (should) => onAlignFinishedRef.current(should)
    });

    const ocr = useOcr({
        allowOcr: props.allowOcr,
        mimeKind: pdf.mimeKind,
        loading: pdf.loading,
        aligning: image.aligning,
        displayUrl: image.displayUrl,
        url: props.url,
        pdfDocRef: pdf.pdfDocRef,
        pageIndex: pdf.pageIndex,
        pageCacheRef: pdf.pageCacheRef,
        imageRef: image.imageRef,
        setHasText: pdf.setHasText,
        setTextReady: pdf.setTextReady,
        setPageCount: pdf.setPageCount,
        setPageIndex: pdf.setPageIndex,
        onClearFind: () => onClearFindRef.current()
    });

    const locate = useLocate({
        findHints: props.findHints || [],
        autoSelectRequired: props.autoSelectRequired,
        url: props.url,
        mimeKind: pdf.mimeKind,
        loading: pdf.loading,
        error: pdf.error,
        textReady: pdf.textReady,
        hasText: pdf.hasText,
        ocrRunning: ocr.ocrRunning,
        aligning: image.aligning,
        allowOcr: props.allowOcr,
        ocrNote: ocr.ocrNote,
        pageCacheRef: pdf.pageCacheRef,
        pageIndex: pdf.pageIndex,
        setPageIndex: pdf.setPageIndex,
        paintHighlights: () => paintHighlightsRef.current(),
        verifyLabel: props.verifyLabel,
        showLocate: props.showLocate,
        scanTick: ocr.scanTick
    });

    const page = usePdfPage({
        mimeKind: pdf.mimeKind,
        loading: pdf.loading,
        pageIndex: pdf.pageIndex,
        scale: viewport.scale,
        pdfDocRef: pdf.pdfDocRef,
        containerRef,
        lastFitRef: viewport.lastFitRef,
        lastBoxRef: viewport.lastBoxRef,
        setFitScale: viewport.setFitScale,
        hitsRef: locate.hitsRef,
        activeIndexRef: locate.activeIndexRef,
        pendingFocusRef: locate.pendingFocusRef,
        panHitIntoView: viewport.panHitIntoView
    });

    paintHighlightsRef.current = page.paintHighlights;
    onCancelRenderRef.current = page.cancel;
    fitImageRef.current = image.fitImageToStage;
    onStatusRef.current = locate.setSearchStatus;
    onAlignFinishedRef.current = (should) => ocr.requestAutoOcr(should);
    onClearFindRef.current = locate.clear;
    onResetRef.current = () => {
        viewport.reset();
        image.reset();
        locate.clear();
        ocr.clear();
        page.clearTextDivs();
    };

    useEffect(() => {
        if (pdf.loading || pdf.mimeKind !== 'pdf') {
            return;
        }

        const id = window.setTimeout(() => {
            viewport.lastFitRef.current = 0;
            viewport.lastBoxRef.current = { w: 0, h: 0 };
            viewport.panRef.current = { x: 0, y: 0 };
            viewport.setPan({ x: 0, y: 0 });
            viewport.setScale(1);
        }, 0);

        return () => window.clearTimeout(id);
    }, [props.url, pdf.loading, pdf.mimeKind]);

    const overlayBoxes = buildOcrOverlayBoxes({
        mimeKind: pdf.mimeKind,
        pageIndex: pdf.pageIndex,
        hits: locate.hits,
        activeIndex: locate.activeIndex,
        ocrBoxes: ocr.ocrBoxes,
        ocrImageSize: ocr.ocrImageSize,
        ocrPageIndex: ocr.ocrPageIndex,
        pageCacheRef: pdf.pageCacheRef
    });

    const height = props.height != null ? props.height : '100%';
    const isPreview = !!props.previewContent;
    const showLocateColumn = !isPreview && props.showLocate !== false;
    const showPagerBar = !!(props.filePager || props.fileActions) && (showLocateColumn || isPreview);
    const toolbarExternal = !isPreview && !!(props.toolbarHost && props.toolbarHost.isConnected);
    const shellClass = [
        'dvShell',
        showLocateColumn ? 'dvShellWithLocate' : '',
        isPreview ? 'dvShellPreview' : '',
        showPagerBar ? 'dvShellWithPager' : '',
        toolbarExternal ? 'dvShellToolbarExternal' : ''
    ].filter(Boolean).join(' ');

    const toolbar = (
        <ViewerToolbar
            hosted={toolbarExternal}
            mimeKind={pdf.mimeKind}
            pageIndex={pdf.pageIndex}
            pageCount={pdf.pageCount}
            setPageIndex={pdf.setPageIndex}
            scale={viewport.scale}
            fitScale={viewport.fitScale}
            viewAway={viewport.viewAway}
            zoomIn={viewport.zoomIn}
            zoomOut={viewport.zoomOut}
            zoomReset={viewport.zoomReset}
            allowRotate={props.allowRotate}
            rotateTurns={image.rotateTurns}
            orientationDirty={image.orientationDirty}
            rotateSaving={image.rotateSaving}
            rotateBy={image.rotateBy}
            saveOrientation={image.saveOrientation}
            onOrientedSave={props.onOrientedSave}
            hitsLength={locate.hits.length}
            searchStatus={locate.searchStatus}
            goPrevHit={locate.goPrevHit}
            goNextHit={locate.goNextHit}
        />
    );

            return (
        <div className={shellClass} style={{ height }}>
            {!isPreview && (toolbarExternal ? createPortal(toolbar, props.toolbarHost as HTMLElement) : toolbar)}
            {showPagerBar && (
                <div className="dvFilePagerBar">
                    {props.filePager ? (
                        <div className="dvFilePager">
                    <button
                        type="button"
                                className="dvFilePagerBtn"
                                disabled={props.filePager.total <= 1 || !props.filePager.canPrev || !!props.filePager.loading}
                                aria-label={`Previous ${props.filePager.label.toLowerCase()}`}
                                onClick={props.filePager.onPrev}
                            >
                                ‹ Previous
                    </button>
                            <span className="dvFilePagerLabel">
                                {props.filePager.label} {props.filePager.current} of {props.filePager.total}
                        </span>
                                    <button
                                        type="button"
                                className="dvFilePagerBtn"
                                disabled={props.filePager.total <= 1 || !props.filePager.canNext || !!props.filePager.loading}
                                aria-label={`Next ${props.filePager.label.toLowerCase()}`}
                                onClick={props.filePager.onNext}
                            >
                                Next ›
                                    </button>
                            </div>
                    ) : null}
                    {props.fileActions ? (
                        <div className="dvFilePagerActions">{props.fileActions}</div>
                    ) : null}
                                </div>
                            )}
            <div className="dvBody">
                {showLocateColumn && (
                    <LocatePanel
                        filePager={undefined}
                        mimeKind={pdf.mimeKind}
                        canSearch={locate.canSearch}
                        locatePending={locate.locatePending}
                        locateUnavailable={locate.locateUnavailable}
                        allowOcr={!!props.allowOcr}
                        ocrRunning={ocr.ocrRunning}
                        aligning={image.aligning}
                        ocrProgress={ocr.ocrProgress}
                        ocrNote={ocr.ocrNote}
                        displayUrl={image.displayUrl}
                        hasPdf={pdf.mimeKind === 'pdf' && !pdf.loading && !pdf.error}
                        hasText={pdf.hasText}
                        textReady={pdf.textReady}
                        customQuery={locate.customQuery}
                        setCustomQuery={locate.setCustomQuery}
                        runCustomSearch={locate.runCustomSearch}
                        runScan={ocr.runScan}
                        searchStatus={locate.searchStatus}
                        locateSource={locate.locateSource}
                        matchedLabel={locate.matchedLabel}
                        verifyProgressLabel={locate.verifyProgressLabel}
                        requiredFields={locate.requiredFields}
                        supportingFields={locate.supportingFields}
                        orphanSupporting={locate.orphanSupporting}
                        hits={locate.hits}
                        activeIndex={locate.activeIndex}
                        activeFieldId={locate.activeFieldId}
                        activeLabel={locate.activeLabel}
                        pageCount={pdf.pageCount}
                        selectMatchedField={locate.selectMatchedField}
                        activateHit={locate.activateHit}
                        collectSoftHits={locate.collectSoftHits}
                        activateLikelyHit={locate.activateLikelyHit}
                    />
                )}
                {isPreview ? (
                    <div className="dvStage dvStagePreview">
                        {props.previewContent}
                                </div>
                ) : (
                    <ViewerStage
                        containerRef={containerRef}
                        canvasRef={page.canvasRef}
                        textLayerRef={page.textLayerRef}
                        imageRef={image.imageRef}
                        loading={pdf.loading}
                        aligning={image.aligning}
                        error={pdf.error}
                        mimeKind={pdf.mimeKind}
                        displayUrl={image.displayUrl}
                        verifyLabel={props.verifyLabel}
                        pan={viewport.pan}
                        rotateTurns={image.rotateTurns}
                        scale={viewport.scale}
                        fitScale={viewport.fitScale}
                        imageNatural={image.imageNatural}
                        setImageNatural={image.setImageNatural}
                        fitImageToStage={image.fitImageToStage}
                        canPan={viewport.canPan}
                        overlayBoxes={overlayBoxes}
                        pendingFocusRef={locate.pendingFocusRef}
                        panHitIntoView={viewport.panHitIntoView}
                        onPointerDown={viewport.onPointerDown}
                        onPointerMove={viewport.onPointerMove}
                        onPointerUp={viewport.onPointerUp}
                        onStageDoubleClick={viewport.onStageDoubleClick}
                    />
                )}
            </div>
        </div>
    );
};

export { DocumentViewer };
export default DocumentViewer;
